import type { JsonObject } from "@arti/dashboard-events";

import { resolveDisplayProvider } from "@arti/utilities";
import { useCallback, useEffect, useRef, useState } from "react";

import type { DashboardOverview } from "../types";

import { getDashboardOverviewAsUser } from "../dashboard-client";
import { formatErrorMessage } from "../utils/errors";
import { isHydratedTaskSnapshot } from "../utils/taskSnapshotGuards";
import { shouldTriggerDashboardDataRefresh } from "./dashboardDataRefreshPolicy";
import { toProviderProgressEventData } from "./providerProgressEntries";
import { useDashboardRealtime } from "./useDashboardRealtime";

interface DashboardState {
  loading: boolean;
  error: string | null;
  data: DashboardOverview | null;
}

const INITIAL_STATE: DashboardState = {
  data: null,
  error: null,
  loading: false,
};

function parseProviderProgressModelInfo(eventData: JsonObject): {
  displayProvider: string | null;
  modelRef: string | null;
  provider: string | null;
} {
  let rawModelRef: null | string = null;
  if (typeof eventData.modelRef === "string") {
    rawModelRef = eventData.modelRef;
  } else if (typeof eventData.model_ref === "string") {
    rawModelRef = eventData.model_ref;
  } else if (typeof eventData.model === "string") {
    rawModelRef = eventData.model;
  }

  const modelRef = rawModelRef?.trim() ? rawModelRef.trim() : null;
  if (modelRef) {
    const provider = resolveDisplayProvider(modelRef, null);
    return { displayProvider: provider, modelRef, provider };
  }

  const rawProvider =
    typeof eventData.provider === "string" ? eventData.provider : null;
  const provider = rawProvider?.trim()
    ? rawProvider.trim().toLowerCase()
    : null;
  return {
    displayProvider: resolveDisplayProvider(null, provider),
    modelRef,
    provider,
  };
}

export function useDashboardData({
  enabled = true,
  onUnauthorized,
  userId,
}: {
  enabled?: boolean;
  onUnauthorized?: () => void;
  userId?: string | null;
} = {}): DashboardState & {
  refresh: () => Promise<void>;
  realtime: { status: string; isHealthy: boolean };
} {
  const [state, setState] = useState<DashboardState>(INITIAL_STATE);
  const eventRefreshTimeoutRef = useRef<number | null>(null);
  const inFlightRequestIdRef = useRef<number | null>(null);
  const latestRequestIdRef = useRef(0);
  const pendingRefreshRef = useRef<{ showLoading: boolean } | null>(null);
  const { status, isHealthy, subscribe } = useDashboardRealtime({ enabled });

  const loadOverview = useCallback(
    async ({ showLoading }: { showLoading: boolean }) => {
      const runLoad = async (nextShowLoading: boolean): Promise<void> => {
        if (!enabled) {
          latestRequestIdRef.current += 1;
          inFlightRequestIdRef.current = null;
          pendingRefreshRef.current = null;
          setState(INITIAL_STATE);
          return;
        }

        if (inFlightRequestIdRef.current !== null) {
          pendingRefreshRef.current = {
            showLoading:
              pendingRefreshRef.current?.showLoading === true ||
              nextShowLoading,
          };
          return;
        }

        const requestId = latestRequestIdRef.current + 1;
        latestRequestIdRef.current = requestId;
        inFlightRequestIdRef.current = requestId;

        if (nextShowLoading) {
          setState((current) => ({ ...current, error: null, loading: true }));
        } else {
          setState((current) => ({ ...current, error: null }));
        }

        try {
          const overviewResult = await getDashboardOverviewAsUser();
          if (latestRequestIdRef.current !== requestId) {
            return;
          }

          setState({ data: overviewResult.data, error: null, loading: false });
        } catch (error) {
          if (latestRequestIdRef.current !== requestId) {
            return;
          }

          if (
            onUnauthorized &&
            error &&
            typeof error === "object" &&
            "status" in error &&
            error.status === 401
          ) {
            onUnauthorized();
          }

          const message = formatErrorMessage(error);
          setState((current) => ({
            ...current,
            error: message,
            loading: false,
          }));
        } finally {
          if (inFlightRequestIdRef.current === requestId) {
            inFlightRequestIdRef.current = null;
          }
        }

        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        const pendingRefresh = pendingRefreshRef.current;
        pendingRefreshRef.current = null;
        if (pendingRefresh) {
          await runLoad(pendingRefresh.showLoading);
        }
      };

      await runLoad(showLoading);
    },
    [enabled, onUnauthorized],
  );

  const refresh = useCallback(
    async () => loadOverview({ showLoading: true }),
    [loadOverview],
  );

  useEffect(() => {
    if (!enabled) {
      latestRequestIdRef.current += 1;
      inFlightRequestIdRef.current = null;
      pendingRefreshRef.current = null;
      setState(INITIAL_STATE);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      void refresh();
    }
  }, [enabled, refresh]);

  useEffect(
    () => () => {
      if (eventRefreshTimeoutRef.current !== null) {
        window.clearTimeout(eventRefreshTimeoutRef.current);
        eventRefreshTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!enabled) {
      return () => {
        // Ignored
      };
    }
    const scheduleRefresh = () => {
      if (eventRefreshTimeoutRef.current !== null) {
        return;
      }
      eventRefreshTimeoutRef.current = window.setTimeout(() => {
        eventRefreshTimeoutRef.current = null;
        loadOverview({ showLoading: false }).catch(() => {
          /* Handled in refresh */
        });
      }, 250);
    };

    const unsubscribe = subscribe(
      (message) => {
        const { type } = message;

        if (
          type === "session_invalidated" &&
          userId &&
          message.payload.user_id === userId
        ) {
          onUnauthorized?.();
          return;
        }

        // Apply lightweight in-memory updates when possible to reduce churn.
        if (type === "task_snapshot" || type === "task_callback") {
          const { payload } = message;
          const { task } = payload;
          if (isHydratedTaskSnapshot(task)) {
            setState((current) => {
              if (!current.data) {
                return current;
              }
              const overview = current.data;
              const existing = overview.tasks.recent;
              const idx = existing.findIndex(
                (entry) => entry.task_id === task.task_id,
              );
              const nextRecent =
                idx !== -1
                  ? [
                      ...existing.slice(0, idx),
                      task,
                      ...existing.slice(idx + 1),
                    ]
                  : [task, ...existing];
              const boundedRecent = nextRecent.slice(
                0,
                Math.max(1, existing.length),
              );
              return {
                ...current,
                data: {
                  ...overview,
                  tasks: { ...overview.tasks, recent: boundedRecent },
                },
              };
            });
          }
        }

        if (type === "provider_progress") {
          const { payload } = message;
          const taskId =
            typeof payload.task_id === "string" ? payload.task_id : null;
          const eventData =
            payload.event_data === undefined
              ? null
              : toProviderProgressEventData(payload.event_data);

          if (taskId && eventData) {
            const modelInfo = parseProviderProgressModelInfo(eventData);
            if (modelInfo.modelRef || modelInfo.provider) {
              setState((current) => {
                if (!current.data) {
                  return current;
                }

                const overview = current.data;
                const existing = overview.tasks.recent;
                const idx = existing.findIndex(
                  (entry) => entry.task_id === taskId,
                );
                if (idx === -1) {
                  return current;
                }

                const task = existing[idx];
                if (!task) {
                  return current;
                }
                const nextTask = {
                  ...task,
                  current_display_provider:
                    modelInfo.displayProvider ?? task.current_display_provider,
                  current_model_ref:
                    modelInfo.modelRef ?? task.current_model_ref,
                  current_model_timestamp_ms:
                    typeof message.timestamp === "number"
                      ? message.timestamp
                      : task.current_model_timestamp_ms,
                  metadata: {
                    ...task.metadata,
                    model: {
                      model: modelInfo.modelRef ?? null,
                      modelRef: modelInfo.modelRef ?? null,
                      provider: modelInfo.provider ?? null,
                    },
                  },
                };
                const nextRecent = [
                  ...existing.slice(0, idx),
                  nextTask,
                  ...existing.slice(idx + 1),
                ];

                return {
                  ...current,
                  data: {
                    ...overview,
                    tasks: { ...overview.tasks, recent: nextRecent },
                  },
                };
              });
            }
          }
        }

        // For aggregated fields (queue summary, plan revisions, task counts), defer to the REST overview snapshot.
        if (shouldTriggerDashboardDataRefresh(message)) {
          scheduleRefresh();
        }
      },
      { topic: "internalEventTopic" },
    );

    return () => unsubscribe();
  }, [enabled, loadOverview, subscribe, userId, onUnauthorized]);

  return { ...state, realtime: { isHealthy, status }, refresh };
}
